/**
 * Sanal kaydırma + blok yükleme için saf blok aritmetiği (docs/DATA-GRID-PLAN.md Faz 5). Satırlar
 * `BLOCK_SIZE`'lık bloklar hâlinde Cube'dan `limit`/`offset` ile gelir; hangi bloğun gerektiği,
 * bir satırın hangi blokta durduğu ve toplamın hangi yanıttan okunacağı burada kararlaştırılır.
 */

/** Tek Cube sorgusuyla yüklenen satır sayısı. */
export const BLOCK_SIZE = 200

/** Sanal gövdede satır yüksekliği (px); `h-10` ile aynı. Satırlar ölçülmez, sabit yüksekliktir. */
export const ROW_HEIGHT = 40

/** Satırın bulunduğu blok (0 tabanlı). */
export const blockOf = (index: number, size: number = BLOCK_SIZE): number =>
  Math.floor(Math.max(index, 0) / size)

/** Bloğun Cube sorgusundaki `limit`/`offset` karşılığı. */
export const blockWindow = (block: number, size: number = BLOCK_SIZE) => ({
  limit: size,
  offset: block * size,
})

/**
 * `[start, end]` (dahil, mutlak satır dizini) aralığını kaplayan bloklar. `total` biliniyorsa son
 * satırın ötesine blok istenmez; bilinmiyorsa (ilk yükleme) yalnız aralığın kendisi.
 */
export function blocksForRange(
  start: number,
  end: number,
  size: number = BLOCK_SIZE,
  total?: number,
): number[] {
  const last = total === undefined ? end : Math.min(end, total - 1)
  if (last < start || last < 0) return []
  const blocks: number[] = []
  for (let block = blockOf(Math.max(start, 0), size); block <= blockOf(last, size); block += 1) {
    blocks.push(block)
  }
  return blocks
}

/** Yüklü bloklar: blok dizini -> o bloğun satırları (düz nesne: TanStack Query yapısal paylaşımı için). */
export type LoadedBlocks<T> = Readonly<Record<number, readonly T[]>>

/** Mutlak satır dizininin yüklü bloklardaki satırı; blok yüklenmemişse `undefined`. */
export function rowAt<T>(
  blocks: LoadedBlocks<T>,
  index: number,
  size: number = BLOCK_SIZE,
): T | undefined {
  if (index < 0) return undefined
  return blocks[blockOf(index, size)]?.[index % size]
}

/** Yüklü (dolu) satır sayısı; eşikli animasyon ve toplam satırı bunu kullanır. */
export function loadedRowCount(blocks: LoadedBlocks<unknown>): number {
  let count = 0
  for (const rows of Object.values(blocks)) count += rows.length
  return count
}

/**
 * Toplam satır sayısı: her blok yanıtı kendi `total`'ını taşır; en son alınanı en günceldir
 * (realtime ile bloklar farklı anlarda yenilenir). Hiç yanıt yoksa 0.
 */
export function pickTotal(entries: readonly { total: number; updatedAt: number }[]): number {
  let best: { total: number; updatedAt: number } | undefined
  for (const entry of entries) {
    if (!best || entry.updatedAt >= best.updatedAt) best = entry
  }
  return best?.total ?? 0
}
